import React, { FC, useEffect } from "react";

import { Trans, useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { useMount } from "react-use";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useSort } from "@/components/atoms";
import { StandardLayout } from "@/components/layouts";
import { InfoBox } from "@/components/molecules";
import { SuccessPage } from "@/components/organisms";
import { ActionButton } from "@/components/organisms/SuccessPage/components/InfoPanel/InfoPanel";
import { PaginatedList } from "@/components/templates";
import usePagination from "@/components/templates/PaginatedList/usePagination";
import { downloadFile } from "@/core/html";
import { packageApi } from "@/shared/apis";
import { getSpApi } from "@/shared/apis/apiRoot";
import { UrlManager } from "@/shared/apis/UrlManager";
import { SortOrder } from "@/shared/domain";
import { defaultPackagePagination } from "@/shared/domain/packages";
import { useAuth, useGlobalFilter, useNavigation, usePolicyholders, useRouterHook } from "@/shared/hooks";
import { useUserFeedback } from "@/userFeedback";

import PackageCard from "./components/PackageCard/PackageCard";
import PackageTable from "./components/PackagesTable/PackagesTable";
import { CONNECTIVE_STATUS_KEY, ConnectiveStatus } from "./constants";
import { usePackagesToSignEvents } from "./usePackagesToSignEvents";
import { getSuccessfullPackageId } from "./utils/getSuccessfullPackageId";

const PackagesToSignPage: FC = () => {
    const { user } = useAuth();
    const {
        search: { dl },
        search,
    } = useRouterHook();
    const { t } = useTranslation();
    const { getAllPolicyholders, getPolicyholder } = usePolicyholders();
    const { setPolicyholdersGF, setIsProEnvironment } = useGlobalFilter();

    const directLink = dl && dl === "true";
    const { id: policyholderIdFromUrl } = useParams<{ id: string }>();

    useEffect(() => {
        if (directLink && policyholderIdFromUrl) {
            if (getAllPolicyholders().length > 1) {
                const policyholder = getPolicyholder(policyholderIdFromUrl);
                if (policyholder) {
                    setPolicyholdersGF([policyholder.policyHolderId]);
                    setIsProEnvironment(!policyholder.isPhysicalPerson);
                }
            }
        }
    }, []);

    const theme = useTheme();
    const packagesToSignEvents = usePackagesToSignEvents();

    const { navToDocuments } = useNavigation();

    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const queryClient = useQueryClient();

    const { page, changePage, changePageSize } = usePagination(true);

    type sortProperty = Paths.ApiDocumentPackagetosign.Get.Parameters.SortProperty;
    const [orderBy, orderByActions] = useSort<sortProperty>({
        sort: { property: "requestDate", order: SortOrder.Descending },
    });

    const { incUsageCount } = useUserFeedback();

    const successfulPackageId = getSuccessfullPackageId(search);
    const { data: packagesPaginated = defaultPackagePagination, isFetching } = useQuery(
        ["packagesPaginated", { ...page, ...orderBy }],
        () =>
            packageApi.getPackages({
                ...(successfulPackageId ? { signedPackageId: successfulPackageId } : {}),
                ...page,
                ...orderByActions.getSort(),
                policyholdersIdList: getAllPolicyholders().map((policyholder) => policyholder.policyHolderId),
            }),
        { cacheTime: 3000, suspense: false },
    );

    useEffect(() => {
        if (CONNECTIVE_STATUS_KEY in search) {
            queryClient.invalidateQueries("documentsPaginated");

            packagesToSignEvents.packagesToSignFinalized(search[CONNECTIVE_STATUS_KEY]);
        }
    }, []);

    useEffect(() => {
        if (!(CONNECTIVE_STATUS_KEY in search)) {
            packagesToSignEvents.packagesToSignStarted();
        }
        return () => packagesToSignEvents.packagesToSignStopped();
    }, []);

    useMount(() => {
        incUsageCount();
    });

    const { result: packages, total: totalItems } = packagesPaginated;
    const totalOfPages = Math.ceil(totalItems / page.pageSize);

    const handlePageSizeChange = (value: number) => {
        // Go back to the 1st page if page size changes
        changePageSize(value);
        changePage(1);
        queryClient.invalidateQueries("packagesPaginated");
    };

    const renderItem = (item: MyBrokerApi.DocumentsPackageToSign) => (
        <PackageCard key={item.guid} packageToSign={item} />
    );

    const renderTable = (items: MyBrokerApi.DocumentsPackageToSign[]) => (
        <PackageTable packages={items} orderBy={orderBy} handleSort={orderByActions.updateSort} />
    );

    const downloadDocument = () => {
        getSpApi()
            .get<Blob>(UrlManager.packagesToSignContent(), {
                responseType: "blob",
            })
            .then((response) => {
                const disposition = response.request.getResponseHeader("Content-Disposition");
                const filename = decodeURI(disposition.match(/(?<=filename=)(.*)(?=;)/g)[0]);
                downloadFile(response.data, filename);
            });
    };

    const navToDocumentsButton: ActionButton = { onClick: navToDocuments, label: t("Common.AccessSignedFiles") };
    const downloadDocumentsButton: ActionButton = { onClick: downloadDocument, label: t("Common.DownloadSignedFiles") };

    const actionButton: ActionButton = user.isScopedLogin() ? downloadDocumentsButton : navToDocumentsButton;

    let message = undefined;

    if (CONNECTIVE_STATUS_KEY in search) {
        if (!isFetching && totalItems === 0) {
            const isSignedOrFinished = [ConnectiveStatus.Signed, ConnectiveStatus.Finished].includes(
                search[CONNECTIVE_STATUS_KEY] as ConnectiveStatus,
            );
            const successMessage = isSignedOrFinished
                ? t("Packages.SignedMessageSuccessPage")
                : t("Packages.RefusedMessageSuccessPage");
            return (
                <StandardLayout title={t("PageTitles.DocumentsToSign")}>
                    <SuccessPage message={successMessage} actionButton={actionButton} />
                </StandardLayout>
            );
        }
        switch (search[CONNECTIVE_STATUS_KEY]) {
            case ConnectiveStatus.Signed:
            case ConnectiveStatus.Finished:
                message = (
                    <InfoBox type="success" data-testid="signed_infobox">
                        <Trans i18nKey="Packages.SignedMessage">
                            Signature effectuée avec succès.{" "}
                            <a className="textUnderline" data-testid="my_documents" onClick={navToDocuments}>
                                Cliquez ici
                            </a>{" "}
                            pour accéder à l'ensemble de vos documents
                        </Trans>
                    </InfoBox>
                );
                break;
            case ConnectiveStatus.Rejected:
            case ConnectiveStatus.Reject:
                message = (
                    <InfoBox type="success" data-testid="refused_infobox">
                        <Trans i18nKey="Packages.RefusedMessage">
                            Refus enregistré avec succès.{" "}
                            <a className="textUnderline" data-testid="my_documents" onClick={navToDocuments}>
                                Cliquez ici
                            </a>{" "}
                            pour accéder à l'ensemble de vos documents
                        </Trans>
                    </InfoBox>
                );
                break;
            case ConnectiveStatus.InvalidToken:
                message = (
                    <InfoBox type="error" data-testid="error_infobox">
                        {t("Packages.ErrorMessage")}
                    </InfoBox>
                );
                break;
        }
    }
    return (
        <PaginatedList<MyBrokerApi.DocumentsPackageToSign>
            message={message}
            page={page}
            tableMode={!isSmallScreen}
            emptyListMessage={t("Documents.Empty")}
            pageTitle={t("PageTitles.DocumentsToSign")}
            renderItem={renderItem}
            renderTable={renderTable}
            isFetchingList={isFetching}
            isLoading={isFetching}
            setPage={changePage}
            totalOfPages={totalOfPages}
            items={packages}
            hideGlobalFilters
            handlePageSizeChange={handlePageSizeChange}
            displayLinkedDocumentsButton={false}
        />
    );
};

export default PackagesToSignPage;
